/*
 * Branding provider — assignment-based + preset-aware.
 *
 * A "preset" is a full brand kit (palette + logo assignments + fonts + font families).
 * Tenants can hold multiple presets (Raptor, Osprey, …) and switch which one is
 * active via the Settings → Branding "Presets" tab. The active preset drives every
 * portal surface. This file exposes:
 *
 *   • PALETTE          — the five approved swatches (locked labels; hexes can be
 *                        overridden per-preset at runtime).
 *   • LOGO_VARIANTS    — the four approved logo lockups (locked)
 *   • COLOR_SLOTS      — the interface slots that consume a palette colour
 *   • LOGO_SLOTS       — the placements that consume a logo variant
 *   • BrandingProvider — fetches the active preset and pushes CSS variables
 *   • useBranding()    — read + preview + refresh the branding state
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { api, getAccessToken } from '@/lib/api';

export type PaletteKey = 'navy' | 'blue' | 'cyan' | 'neutral' | 'white';
// The four fixed lockups plus any operator-added ``custom_N`` library logos.
export type FixedLogoVariant = 'primary' | 'mono_white' | 'mono_navy' | 'mark';
export type LogoVariant = FixedLogoVariant | (string & {});

/** A named custom logo in a preset's library (up to 10). */
export interface CustomLogo { key: string; name: string }

export const PALETTE: Record<PaletteKey, { label: string; hex: string }> = {
  navy: { label: 'Primary navy', hex: '#061935' },
  blue: { label: 'Primary blue', hex: '#0D63FF' },
  cyan: { label: 'Accent cyan', hex: '#36EBFE' },
  neutral: { label: 'Neutral grey', hex: '#E5E8E8' },
  white: { label: 'White', hex: '#FFFFFF' },
};

export const LOGO_VARIANTS: Record<FixedLogoVariant, string> = {
  primary: 'Primary (colour on white)',
  mono_white: 'Monochrome white (for dark surfaces)',
  mono_navy: 'Monochrome navy (for light surfaces)',
  mark: 'Icon-only mark',
};

// Slots that accept a palette colour. Each has a default and a hint.
export interface ColorSlot { key: ColorSlotKey; label: string; hint: string }
export type ColorSlotKey =
  | 'header_bg' | 'header_text'
  | 'sidebar_bg' | 'sidebar_text'
  | 'sidebar_active_bg' | 'sidebar_active_text'
  | 'primary_accent'
  | 'gradient_from' | 'gradient_to'
  | 'focus_ring';

export const COLOR_SLOTS: ColorSlot[] = [
  { key: 'header_bg', label: 'Header background', hint: 'Top bar background across every portal.' },
  { key: 'header_text', label: 'Header text', hint: 'Text and icons on the top bar.' },
  { key: 'sidebar_bg', label: 'Sidebar background', hint: 'Left navigation panel background.' },
  { key: 'sidebar_text', label: 'Sidebar text', hint: 'Default text colour for sidebar navigation items.' },
  { key: 'sidebar_active_bg', label: 'Sidebar active background', hint: 'Highlight behind the currently selected sidebar item. Rendered at ~15% opacity.' },
  { key: 'sidebar_active_text', label: 'Sidebar active text', hint: 'Text colour of the currently selected sidebar item.' },
  { key: 'primary_accent', label: 'Primary accent', hint: 'Primary buttons and active links across the platform.' },
  { key: 'gradient_from', label: 'Gradient (start)', hint: 'Starting colour of the hero gradient.' },
  { key: 'gradient_to', label: 'Gradient (end)', hint: 'Ending colour of the hero gradient.' },
  { key: 'focus_ring', label: 'Focus ring', hint: 'Ring shown when a control is focused for accessibility.' },
];

// Slots that accept a logo variant.
export interface LogoSlot { key: LogoSlotKey; label: string; hint: string }
export type LogoSlotKey =
  | 'sidebar_logo' | 'login_logo' | 'email_header_logo'
  | 'pdf_header_logo' | 'favicon' | 'login_background_mark'
  | 'survey_header_logo' | 'confirmation_letter_logo';

export const LOGO_SLOTS: LogoSlot[] = [
  { key: 'sidebar_logo', label: 'Sidebar (top-left)', hint: 'Appears in the top-left of every portal sidebar.' },
  { key: 'login_logo', label: 'Login page', hint: 'Displayed on the sign-in card.' },
  { key: 'email_header_logo', label: 'Email header', hint: 'Used on outbound transactional emails.' },
  { key: 'pdf_header_logo', label: 'PDF export header', hint: 'Applied to Confirmation of Cover, Bordereaux, and other exports.' },
  { key: 'favicon', label: 'Favicon (browser tab)', hint: 'Small square icon shown in browser tabs and bookmarks.' },
  { key: 'login_background_mark', label: 'Login background mark', hint: 'Watermark mark that sits behind the sign-in card.' },
];

/** System-generated documents recipients see — each can pick its own logo variant. */
export const DOCUMENT_LOGO_SLOTS: LogoSlot[] = [
  { key: 'survey_header_logo', label: 'Survey (recipient link)', hint: 'Shown on the public survey page recipients open from their emailed/SMS link. Sits on the dark brand header — a light/mono-white mark reads best.' },
  { key: 'confirmation_letter_logo', label: 'Letter of Confirmation', hint: 'Header logo on the Confirmation of Cover letter. Sits on the letter’s dark brand header — a light/mono-white mark reads best.' },
];

export const DEFAULT_COLOR_ASSIGNMENTS: Record<ColorSlotKey, PaletteKey> = {
  header_bg: 'white', header_text: 'navy',
  sidebar_bg: 'white', sidebar_text: 'navy',
  sidebar_active_bg: 'blue', sidebar_active_text: 'navy',
  primary_accent: 'blue',
  gradient_from: 'blue', gradient_to: 'cyan',
  focus_ring: 'blue',
};

export const DEFAULT_LOGO_ASSIGNMENTS: Record<LogoSlotKey, LogoVariant> = {
  sidebar_logo: 'primary', login_logo: 'primary',
  email_header_logo: 'primary', pdf_header_logo: 'primary',
  favicon: 'mark', login_background_mark: 'mark',
  survey_header_logo: 'mono_white', confirmation_letter_logo: 'mono_white',
};

/** A preset font (uploaded by the operator). */
export interface BrandFont {
  id: string;
  family: string;
  style: string;  // 'normal' | 'italic' | ...
  weight: number; // 100-900
  data_url: string; // 'data:font/woff2;base64,...'
  display?: string;
}

export interface Branding {
  color_assignments: Record<ColorSlotKey, PaletteKey>;
  logo_assignments: Record<LogoSlotKey, LogoVariant>;
  authorised_signature_data_url?: string | null;
  authorised_signatory_name?: string | null;
  authorised_signatory_title?: string | null;
  // Preset-level fields (populated when the public endpoint returns an active preset).
  preset_id?: string;
  preset_name?: string;
  primary_navy?: string;
  primary_blue?: string;
  accent_cyan?: string;
  neutral_grey?: string;
  gradient_from_hex?: string;
  gradient_to_hex?: string;
  font_family_display?: string;
  font_family_body?: string;
  font_family_mono?: string;
  fonts?: BrandFont[];
  // Per-variant uploaded logo data URLs — 'primary' / 'mono_white' / 'mono_navy' / 'mark',
  // plus 'custom_N' keys pointing at served URLs for library logos.
  logo_variants?: Partial<Record<string, string>>;
  // Operator's custom logo library (up to 10 named logos) for this preset.
  custom_logos?: CustomLogo[];
  // Text lockup shown next to the mark. Osprey → "OSPREY"; Raptor → "RAPTOR".
  wordmark_text?: string;
  wordmark_tagline?: string;
  // Font family stack the wordmark text renders with. Falls back to the
  // preset's display font when blank.
  wordmark_font_family?: string;
  // Font family stack the tagline renders with. Falls back to the
  // preset's body font when blank.
  tagline_font_family?: string;
}

const DEFAULT_BRANDING: Branding = {
  color_assignments: { ...DEFAULT_COLOR_ASSIGNMENTS },
  logo_assignments: { ...DEFAULT_LOGO_ASSIGNMENTS },
  primary_navy: PALETTE.navy.hex,
  primary_blue: PALETTE.blue.hex,
  accent_cyan: PALETTE.cyan.hex,
  neutral_grey: PALETTE.neutral.hex,
  gradient_from_hex: PALETTE.blue.hex,
  gradient_to_hex: PALETTE.cyan.hex,
  font_family_display: "'Goldman', 'Barlow', sans-serif",
  font_family_body: "'Nunito', system-ui, sans-serif",
  font_family_mono: "'Barlow', sans-serif",
  fonts: [],
};

/** Build the effective palette hexes for a preset. Preset color overrides
 *  win over the locked defaults so an Osprey navy is *actually* Osprey navy. */
const presetPalette = (b: Branding): Record<PaletteKey, string> => ({
  navy: b.primary_navy || PALETTE.navy.hex,
  blue: b.primary_blue || PALETTE.blue.hex,
  cyan: b.accent_cyan || PALETTE.cyan.hex,
  neutral: b.neutral_grey || PALETTE.neutral.hex,
  white: PALETTE.white.hex,
});

/** Convert a palette assignment into a concrete CSS value for a slot,
 *  respecting per-preset color overrides. `sidebar_active_bg` renders as
 *  a ~15 % translucent tint of the chosen colour so the active indicator
 *  sits softly behind the sidebar text. */
const resolveSlot = (slot: ColorSlotKey, key: PaletteKey, palette: Record<PaletteKey, string>): string => {
  const hex = palette[key] ?? palette.blue;
  if (slot === 'sidebar_active_bg') {
    if (key === 'white') return 'rgba(255, 255, 255, 0.25)';
    return `${hex}26`;
  }
  return hex;
};

// Google Fonts loader for the *locked* Raptor face fallbacks. Uploaded
// preset fonts are injected separately via @font-face below.
const loadedFontFamilies = new Set<string>();
const loadFontFamily = (family: string): void => {
  if (!family || loadedFontFamilies.has(family)) return;
  loadedFontFamilies.add(family);
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.setAttribute('data-branding-font', family);
  document.head.appendChild(link);
};

/** Inject @font-face rules for every uploaded font on the active preset.
 *  We reuse a single <style data-brand-fonts> node so switching presets
 *  fully replaces the previous face set — no stale families lingering.
 *
 *  Style values coming from the operator are richer than pure CSS
 *  `font-style` — we accept "condensed" and "condensed italic" too and
 *  translate them into the proper CSS pair (font-style + font-stretch)
 *  so a D-DIN Condensed face registers as `font-stretch: condensed`
 *  and picks up automatically via `font-family: 'D-DIN'`. */
const injectPresetFonts = (fonts: BrandFont[]): void => {
  const id = 'brand-fonts';
  let styleEl = document.getElementById(id) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = id;
    styleEl.setAttribute('data-brand-fonts', 'true');
    document.head.appendChild(styleEl);
  }
  const parseStyle = (raw: string | undefined): { style: string; stretch: string } => {
    const s = (raw || 'normal').toLowerCase().trim();
    const italic = s.includes('italic');
    const condensed = s.includes('condensed');
    const expanded = s.includes('expanded');
    return {
      style: italic ? 'italic' : 'normal',
      stretch: condensed ? 'condensed' : expanded ? 'expanded' : 'normal',
    };
  };
  const rules = (fonts || []).map((f) => {
    // Sanitise: only trust data:font/* URLs coming from our backend.
    if (!f?.data_url || !f.data_url.startsWith('data:font/')) return '';
    const family = f.family?.replace(/'/g, "\\'") || 'Custom';
    const weight = Number.isFinite(f.weight) ? f.weight : 400;
    const { style, stretch } = parseStyle(f.style);
    const display = f.display || 'swap';
    return `@font-face { font-family: '${family}'; font-style: ${style}; font-stretch: ${stretch}; font-weight: ${weight}; font-display: ${display}; src: url(${f.data_url}); }`;
  }).filter(Boolean);
  styleEl.textContent = rules.join('\n');
};

/** Inject a small stylesheet that binds `.font-headline`, `.font-body`,
 *  `.font-tabular` to CSS variables. Combined with the per-preset font
 *  family strings this lets Osprey's D-DIN faces (once uploaded) take
 *  over from Raptor's Goldman/Nunito/Barlow immediately. */
const injectBrandFontRules = (): void => {
  const id = 'brand-font-rules';
  if (document.getElementById(id)) return;
  const el = document.createElement('style');
  el.id = id;
  el.setAttribute('data-brand-font-rules', 'true');
  el.textContent = `
    .font-headline { font-family: var(--brand-font-display, 'Goldman', 'Barlow', sans-serif); }
    .font-body { font-family: var(--brand-font-body, 'Nunito', system-ui, sans-serif); }
    .font-tabular { font-family: var(--brand-font-mono, 'Barlow', sans-serif); font-variant-numeric: tabular-nums; }
    body { font-family: var(--brand-font-body, 'Nunito', system-ui, sans-serif); }
  `.trim();
  document.head.appendChild(el);
};

const applyBranding = (b: Branding): void => {
  const r = document.documentElement.style;
  const palette = presetPalette(b);
  // Base palette CSS vars — kept in sync so raw var consumers stay on brand.
  r.setProperty('--raptor-navy', palette.navy);
  r.setProperty('--raptor-blue', palette.blue);
  r.setProperty('--raptor-cyan', palette.cyan);
  r.setProperty('--raptor-neutral', palette.neutral);
  // Slot CSS vars — every layout consumes these.
  r.setProperty('--raptor-header-bg', resolveSlot('header_bg', b.color_assignments.header_bg, palette));
  r.setProperty('--raptor-header-text', resolveSlot('header_text', b.color_assignments.header_text, palette));
  r.setProperty('--raptor-sidebar-bg', resolveSlot('sidebar_bg', b.color_assignments.sidebar_bg, palette));
  r.setProperty('--raptor-sidebar-text', resolveSlot('sidebar_text', b.color_assignments.sidebar_text, palette));
  r.setProperty('--raptor-sidebar-active-bg', resolveSlot('sidebar_active_bg', b.color_assignments.sidebar_active_bg, palette));
  r.setProperty('--raptor-sidebar-active-text', resolveSlot('sidebar_active_text', b.color_assignments.sidebar_active_text, palette));
  r.setProperty('--raptor-accent', resolveSlot('primary_accent', b.color_assignments.primary_accent, palette));
  r.setProperty('--raptor-gradient-from', resolveSlot('gradient_from', b.color_assignments.gradient_from, palette));
  r.setProperty('--raptor-gradient-to', resolveSlot('gradient_to', b.color_assignments.gradient_to, palette));
  r.setProperty('--raptor-focus-ring', resolveSlot('focus_ring', b.color_assignments.focus_ring, palette));
  // Preset fonts — set CSS variables + inject @font-face for uploaded files.
  r.setProperty('--brand-font-display', b.font_family_display || "'Goldman', 'Barlow', sans-serif");
  r.setProperty('--brand-font-body', b.font_family_body || "'Nunito', system-ui, sans-serif");
  r.setProperty('--brand-font-mono', b.font_family_mono || "'Barlow', sans-serif");
  injectBrandFontRules();
  injectPresetFonts(b.fonts || []);
  // Always preload the Raptor Google fallback fonts so tags without preset
  // fonts still render on-brand.
  loadFontFamily('Goldman');
  loadFontFamily('Nunito');
  loadFontFamily('Barlow');
};

interface BrandingCtx {
  branding: Branding;
  refresh: () => Promise<void>;
  applyPreview: (b: Partial<Branding>) => void;
}

const Ctx = createContext<BrandingCtx>({ branding: DEFAULT_BRANDING, refresh: async () => {}, applyPreview: () => {} });

const BRAND_CACHE_KEY = 'raptor:branding:v2';
const BRAND_CACHE_TTL_MS = 5 * 60_000; // Branding rarely changes; caching cuts /public/settings/branding traffic on route navigations.

const readBrandCache = (): Branding | null => {
  try {
    const raw = localStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at: number; data: Branding };
    if (!parsed?.at || Date.now() - parsed.at > BRAND_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
};
const writeBrandCache = (data: Branding): void => {
  try { localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify({ at: Date.now(), data })); } catch { /* quota exceeded, ignore */ }
};

/** Public helper — clears the cached active preset so the next mount
 *  refetches from the server. Called after activating a new preset. */
export const invalidateBrandingCache = (): void => {
  try { localStorage.removeItem(BRAND_CACHE_KEY); } catch { /* ignore */ }
  // Also clear the legacy v1 key if it lingers from an older session.
  try { localStorage.removeItem('raptor:branding:v1'); } catch { /* ignore */ }
};

const mergePayload = (raw: any): Branding => ({
  color_assignments: { ...DEFAULT_COLOR_ASSIGNMENTS, ...(raw?.color_assignments || {}) },
  logo_assignments: { ...DEFAULT_LOGO_ASSIGNMENTS, ...(raw?.logo_assignments || {}) },
  authorised_signature_data_url: raw?.authorised_signature_data_url ?? null,
  authorised_signatory_name: raw?.authorised_signatory_name ?? null,
  authorised_signatory_title: raw?.authorised_signatory_title ?? null,
  preset_id: raw?.id || raw?.preset_id,
  preset_name: raw?.name || raw?.preset_name,
  primary_navy: raw?.primary_navy,
  primary_blue: raw?.primary_blue,
  accent_cyan: raw?.accent_cyan,
  neutral_grey: raw?.neutral_grey,
  gradient_from_hex: raw?.gradient_from,
  gradient_to_hex: raw?.gradient_to,
  font_family_display: raw?.font_family_display,
  font_family_body: raw?.font_family_body,
  font_family_mono: raw?.font_family_mono,
  fonts: Array.isArray(raw?.fonts) ? raw.fonts : [],
  logo_variants: (raw?.logo_variants && typeof raw.logo_variants === 'object') ? raw.logo_variants : {},
  custom_logos: Array.isArray(raw?.custom_logos) ? raw.custom_logos : [],
  wordmark_text: raw?.wordmark_text,
  wordmark_tagline: raw?.wordmark_tagline,
  wordmark_font_family: raw?.wordmark_font_family,
  tagline_font_family: raw?.tagline_font_family,
});

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Seed from localStorage so branded colours + fonts + logos are correct
  // on the very first frame — no more brand flash / theme flicker.
  const [branding, setBranding] = useState<Branding>(() => {
    const cached = readBrandCache();
    if (cached) {
      applyBranding(cached);
      return cached;
    }
    applyBranding(DEFAULT_BRANDING);
    return DEFAULT_BRANDING;
  });

  const refresh = async (): Promise<void> => {
    try {
      // When signed in, fetch the TENANT's active branding (isolated per
      // insurer). Pre-auth, fall back to the platform Raptor default so the
      // login page always carries the Raptor identity.
      let raw: any;
      if (getAccessToken()) {
        raw = (await api.get<any>('/settings/branding-active')).data;
      } else {
        const base = process.env.REACT_APP_BACKEND_URL;
        raw = (await axios.get<any>(`${base}/api/public/settings/branding`)).data;
      }
      const merged = mergePayload(raw);
      setBranding(merged);
      applyBranding(merged);
      writeBrandCache(merged);
    } catch {
      applyBranding(DEFAULT_BRANDING);
    }
  };

  // Refetch branding whenever auth state changes (login / tenant switch /
  // exit impersonation / logout) so the surface instantly reflects the
  // correct tenant's brand — never a stale or cross-tenant one.
  useEffect(() => {
    const onAuthChange = (): void => { invalidateBrandingCache(); void refresh(); };
    window.addEventListener('raptor:branding-refresh', onAuthChange);
    return () => window.removeEventListener('raptor:branding-refresh', onAuthChange);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Only hit the network when the cache is missing/stale — the loaded
  // cache already covers the vast majority of navigations.
  useEffect(() => {
    if (!readBrandCache()) refresh();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const applyPreview = (partial: Partial<Branding>): void => {
    const merged: Branding = {
      ...branding,
      ...partial,
      color_assignments: { ...branding.color_assignments, ...(partial.color_assignments || {}) },
      logo_assignments: { ...branding.logo_assignments, ...(partial.logo_assignments || {}) },
      fonts: partial.fonts !== undefined ? partial.fonts : branding.fonts,
    };
    setBranding(merged);
    applyBranding(merged);
  };

  const value = useMemo(() => ({ branding, refresh, applyPreview }), [branding]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useBranding = (): BrandingCtx => useContext(Ctx);

/** Read helper — the logo variant assigned to a given slot. */
export const useLogoVariant = (slot: LogoSlotKey): LogoVariant => {
  const { branding } = useBranding();
  return branding.logo_assignments[slot] ?? DEFAULT_LOGO_ASSIGNMENTS[slot];
};

/** All selectable logo options (4 fixed + the preset's custom library),
 *  as [key, label] pairs — used to build every logo assignment dropdown. */
export const logoOptions = (custom?: CustomLogo[]): Array<[LogoVariant, string]> => {
  const fixed = (Object.entries(LOGO_VARIANTS) as Array<[FixedLogoVariant, string]>);
  const customs = (custom || []).map((c) => [c.key, c.name] as [LogoVariant, string]);
  return [...fixed, ...customs];
};
